/**
 * Token Firebase con mutex y cooldown — evita refresh en loop (SoluGo hotfix).
 */
import { auth } from './firebase';

const TOKEN_COOLDOWN_MS = 60_000;
/** Firebase ID token ~1h; refrescar antes en panel/PWA instalada. */
export const PROACTIVE_TOKEN_REFRESH_MS = 50 * 60 * 1000;

let tokenRefreshPromise = null;
let lastTokenErrorAt = 0;
let lastTokenWarnAt = 0;
let authTokenUnavailable = false;
let authReadyPromise = null;

async function ensureAuthReady() {
  if (!authReadyPromise) {
    authReadyPromise = auth.authStateReady().catch(() => undefined);
  }
  await authReadyPromise;
}

function warnTokenOnce(message) {
  const now = Date.now();
  if (now - lastTokenWarnAt < TOKEN_COOLDOWN_MS) return;
  lastTokenWarnAt = now;
  console.warn('[auth]', message);
}

export function isAuthTokenUnavailable() {
  return authTokenUnavailable;
}

export function resetAuthTokenState() {
  authTokenUnavailable = false;
  lastTokenErrorAt = 0;
  tokenRefreshPromise = null;
}

/**
 * @param {boolean} forceRefresh - solo true en retry 401/403
 */
export async function getAuthBearerToken(forceRefresh = false) {
  await ensureAuthReady();
  const user = auth.currentUser;
  if (!user) {
    // JWT en caché sin sesión Firebase → evita 401 en loop y logout falso en PWA iOS.
    if (forceRefresh) return null;
    const cached = localStorage.getItem('authToken');
    return cached || null;
  }

  const now = Date.now();
  if (
    !forceRefresh &&
    authTokenUnavailable &&
    now - lastTokenErrorAt < TOKEN_COOLDOWN_MS
  ) {
    const cached = localStorage.getItem('authToken');
    return cached || null;
  }

  if (tokenRefreshPromise) {
    try {
      return await tokenRefreshPromise;
    } catch {
      const cached = localStorage.getItem('authToken');
      return cached || null;
    }
  }

  tokenRefreshPromise = (async () => {
    try {
      const token = await user.getIdToken(forceRefresh);
      if (token) {
        localStorage.setItem('authToken', token);
        authTokenUnavailable = false;
        return token;
      }
      return localStorage.getItem('authToken');
    } catch (error) {
      authTokenUnavailable = true;
      lastTokenErrorAt = Date.now();
      const code = String(error?.code || '');
      const msg = String(error?.message || error);
      if (
        code.includes('network') ||
        msg.includes('network-request-failed') ||
        msg.includes('auth/network')
      ) {
        warnTokenOnce('Token refresh no disponible (red). Usando sesión en caché si existe.');
      } else {
        warnTokenOnce(`Token refresh falló: ${msg}`);
      }
      return localStorage.getItem('authToken');
    } finally {
      tokenRefreshPromise = null;
    }
  })();

  return tokenRefreshPromise;
}
